import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  DownloadSimple, Files, EnvelopeSimple, ChatCircleText, Archive,
  Spinner, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "@/lib/api";

/**
 * Case-file export dropdown (solicitor-only). Surfaces four ZIP downloads:
 *  • Documents — all originals + MANIFEST.csv
 *  • Emails — chronological PDF transcript
 *  • Messages — chronological PDF transcript (client + referrer channels)
 *  • Everything — mega-ZIP with documents/, emails.pdf, messages.pdf and a
 *    case-summary.pdf cover sheet.
 *
 * Backend builds the ZIP server-side (see ``backend/routes/case_export.py``),
 * we stream the response into a blob and trigger a browser download. The
 * file is never persisted on disk client-side, so this is safe for
 * unattended large transactions.
 */
const EXPORTS = [
  { kind: "documents", label: "Documents (originals)", icon: Files,
    hint: "ZIP of every file, kept in its original folders + MANIFEST.csv" },
  { kind: "emails", label: "Emails (.eml + PDF)", icon: EnvelopeSimple,
    hint: "One .eml per email (opens in Outlook) + combined emails.pdf" },
  { kind: "messages", label: "Messages (transcript)", icon: ChatCircleText,
    hint: "Client + referrer channels combined PDF" },
  { kind: "everything", label: "Everything", icon: Archive,
    hint: "Foldered documents/ + emails/ .eml + emails.pdf + messages.pdf" },
];

export default function CaseExportMenu({ caseId, caseRef, caseTitle }) {
  const [busy, setBusy] = useState(null); // which kind is currently downloading

  const run = async (kind) => {
    if (busy) return;
    setBusy(kind);
    const toastId = toast.loading(`Preparing ${kind} export…`);
    try {
      // Stream response as a blob — Axios respects responseType so we can
      // bridge to a temporary <a download> link.
      const res = await API.get(`/cases/${caseId}/export/${kind}`, {
        responseType: "blob",
        timeout: 0, // big cases can take >30s to ZIP; let the server decide.
      });
      // Try to use the server-suggested filename from Content-Disposition;
      // fall back to a sensible default.
      const cd = res.headers["content-disposition"] || "";
      let filename = `${caseRef || caseTitle || "case"}_${kind}.zip`;
      const m = /filename="?([^";]+)"?/.exec(cd);
      if (m) filename = m[1];

      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`, { id: toastId });
    } catch (err) {
      // Try to parse a server JSON error inside the blob (axios will treat
      // the error body as a blob too if responseType=blob).
      let msg = "Export failed";
      const data = err.response?.data;
      if (data && data.type !== "application/zip") {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {
          // ignore parse failures, fall back to the generic msg
        }
      } else if (err.message) {
        msg = err.message;
      }
      toast.error(msg, { id: toastId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        data-testid="case-export-everything-quick-btn"
        title="Download the full case file (documents in folders, .eml emails, PDFs)"
        disabled={!!busy}
        onClick={() => run("everything")}
        className="h-8 px-2.5 inline-flex items-center gap-1 text-slate-700 hover:bg-slate-50 text-xs font-medium transition-colors disabled:opacity-50"
      >
        {busy === "everything"
          ? <Spinner weight="bold" className="w-3.5 h-3.5 animate-spin" />
          : <DownloadSimple weight="bold" className="w-3.5 h-3.5" />}
        Export
      </button>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="case-export-menu-btn"
            title="More export options"
            disabled={!!busy}
            className="h-8 px-1.5 border-l border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <CaretDown weight="bold" className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-1" align="end">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-2 pt-1.5 pb-0.5">
            Export this case file
          </p>
          {EXPORTS.map(({ kind, label, icon: Icon, hint }) => (
            <button
              key={kind}
              type="button"
              disabled={!!busy}
              onClick={() => run(kind)}
              data-testid={`case-export-${kind}-btn`}
              className={`w-full flex items-start gap-2 px-2 py-1.5 rounded text-left ${
                busy === kind ? "bg-slate-100" : "hover:bg-slate-50"
              } ${busy && busy !== kind ? "opacity-40" : ""}`}
            >
              <div className="w-6 h-6 mt-0.5 flex items-center justify-center rounded bg-slate-100 text-slate-600 shrink-0">
                {busy === kind
                  ? <Spinner weight="bold" className="w-3.5 h-3.5 animate-spin" />
                  : <Icon weight="bold" className="w-3.5 h-3.5" />}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800">{label}</p>
                <p className="text-[10px] text-slate-500 leading-snug">{hint}</p>
              </div>
            </button>
          ))}
          <p className="text-[9px] text-slate-400 px-2 pt-1.5 pb-1 border-t border-slate-100 mt-1">
            Large cases may take 10–30 seconds to bundle.
          </p>
        </PopoverContent>
      </Popover>
    </div>
  );
}
